import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { GlossaryDetailPage } from "@/components/calv/glossary/GlossaryDetailPage";
import { GlossaryNotFound } from "@/components/calv/glossary/GlossaryNotFound";
import type { GlossaryEntry } from "@/lib/glossary";
import { getGlossaryDetail, type GlossaryDetail } from "@/lib/glossary-content";
import { SITE_URL, pathMap } from "@/lib/locale";
import { pageHead } from "@/lib/seo";

type LoaderData = {
  entry: GlossaryEntry;
  detail: GlossaryDetail | null;
  alternateLocalePath: string;
  alternateSlug: string | null;
};

export const Route = createFileRoute("/en/glossary/$slug")({
  // C3: glossary.ts loaded dynamically inside the loader — see /glossar/$slug.
  loader: async ({ params }): Promise<LoaderData> => {
    if (
      params.slug.startsWith("$") ||
      params.slug.startsWith("%24") ||
      params.slug === "[slug]"
    ) {
      throw redirect({ to: "/en/glossary", statusCode: 301 });
    }
    const { getGlossaryEntryBySlug, getGlossaryAlternateSlug } = await import(
      "@/lib/glossary"
    );
    const entry = getGlossaryEntryBySlug(params.slug, "en");
    if (!entry) throw notFound();
    if (entry.tier === 2) {
      throw redirect({ to: "/en/glossary", hash: entry.id });
    }
    const alternateSlug = getGlossaryAlternateSlug(entry, "de");
    const alternateLocalePath = alternateSlug
      ? `${pathMap.glossary.de}/${alternateSlug}`
      : pathMap.glossary.de;
    const detail = await getGlossaryDetail(entry.id, "en");
    return { entry, detail, alternateLocalePath, alternateSlug };
  },
  head: ({ loaderData }) => {
    const entry = loaderData?.entry;
    if (!entry || entry.tier !== 1) {
      const h = pageHead("glossary", "en");
      return { meta: h.meta, links: h.links };
    }
    const detail = loaderData?.detail;
    const deSlug = loaderData?.alternateSlug;
    const canonical = `${SITE_URL}${pathMap.glossary.en}/${entry.slug.en}`;
    const altDe = deSlug
      ? `${SITE_URL}${pathMap.glossary.de}/${deSlug}`
      : `${SITE_URL}${pathMap.glossary.de}`;
    const title = detail?.fm.seoTitle ?? `${entry.term.en} — Glossary — Calvarius`;
    const description = detail?.fm.metaDescription ?? entry.shortDefinition.en;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: "en_US" },
        { property: "og:locale:alternate", content: "de_DE" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [
        { rel: "canonical", href: canonical },
        { rel: "alternate", hrefLang: "de", href: altDe },
        { rel: "alternate", hrefLang: "en", href: canonical },
        { rel: "alternate", hrefLang: "x-default", href: altDe },
      ],
    };
  },
  component: GlossaryRoute,
  notFoundComponent: () => <GlossaryNotFound locale="en" />,
});

function GlossaryRoute() {
  const { entry, detail } = Route.useLoaderData();
  if (!detail) throw notFound();
  return <GlossaryDetailPage entry={entry} detail={detail} locale="en" />;
}
